import type { ArticleCategory, ArticleCategoryShallow } from "@brookbush/types";

import type { GetStaticPaths, GetStaticProps } from "next";
import { NextSeo } from "next-seo";

import type { BreadcrumbItem } from "~/components/Breadcrumbs";
import { GridLayout } from "~/layouts";
import ArticlesCategories from "~/pages/articles/components/ArticlesCategories";
import ArticlesCategory from "~/pages/articles/components/ArticlesCategory";
import type { NextPageWithProps } from "~/types";
import fetcher from "~/utils/fetcher.client";

interface ArticlesCategoryPageProps {
	category: ArticleCategory;
	shallowCategories: ArticleCategoryShallow[];
}

const ArticlesCategoryPage: NextPageWithProps<ArticlesCategoryPageProps> = ({
	category,
	shallowCategories,
}) => {
	return (
		<>
			<NextSeo
				title={`${category.title} Articles`}
				description={`Brookbush Institute ${category.title} Articles`}
			/>
			<GridLayout.Sidebar className="z-20 laptop:block hidden">
				<ArticlesCategories shallowCategories={shallowCategories} />
			</GridLayout.Sidebar>

			<GridLayout.Body>
				<ArticlesCategory category={category} />
			</GridLayout.Body>
		</>
	);
};

ArticlesCategoryPage.Layout = GridLayout;

export const getStaticPaths: GetStaticPaths = async () => {
	return {
		paths: [],
		fallback: "blocking",
	};
};

export const getStaticProps: GetStaticProps<
	any,
	{
		slugs: string[];
	}
> = async ({ params }) => {
	if (!params?.slugs.length) {
		return {
			notFound: true,
		};
	}

	const [shallowCategories, category] = await Promise.all([
		fetcher("/articles/categories/shallow"),
		fetcher(`/articles/categories/${params!.slugs.join("/")}`),
	]);

	const selectedCategory: ArticleCategoryShallow | undefined = (
		shallowCategories as ArticleCategoryShallow[]
	).find((category) => category.slug === params?.slugs[0]);

	const selectedSubCategory = selectedCategory?.subCategories?.find(
		(subCategory) => subCategory.slug === params?.slugs[1],
	);

	return {
		props: {
			shallowCategories,
			category,
			breadcrumbs: [
				{ name: "Articles", href: "/articles" },
				{ name: "Categories", href: "/articles" },
				selectedCategory
					? {
							name: selectedCategory?.title,
							href: `/articles/categories/${selectedCategory?.slug}`,
						}
					: undefined,
				selectedSubCategory
					? {
							name: selectedSubCategory?.title,
							href: `/articles/categories/${selectedCategory?.slug}/${selectedSubCategory.slug}`,
						}
					: undefined,
			].filter(Boolean) as BreadcrumbItem[],
		},
	};
};

export default ArticlesCategoryPage;
